import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLicitaciones, useProyectos } from '../lib/consultas';

interface Resultado {
  etiqueta: string;
  tag: string;
  ir: () => void;
}

const DESTINOS = [
  { etiqueta: 'Dashboard general', ruta: '/' },
  { etiqueta: 'Proyectos activos', ruta: '/proyectos' },
  { etiqueta: 'Control de costos', ruta: '/costos' },
  { etiqueta: 'Facturación y cobranza', ruta: '/cobranza' },
  { etiqueta: 'Pipeline', ruta: '/pipeline' },
  { etiqueta: 'Calendario', ruta: '/calendario' },
  { etiqueta: 'Organismos', ruta: '/organismos' },
  { etiqueta: 'Competencia', ruta: '/competencia' },
];

/**
 * Paleta de comandos (Ctrl+K), conservada del CRM original.
 *
 * Busca en pestañas, proyectos y licitaciones a la vez. Con un catálogo de este
 * tamaño el filtrado en cliente es instantáneo y evita una petición por tecla.
 */
export function PaletaComandos({ alCerrar }: { alCerrar: () => void }) {
  const navegar = useNavigate();
  const [consulta, setConsulta] = useState('');
  const [indice, setIndice] = useState(0);
  const refEntrada = useRef<HTMLInputElement>(null);

  const { data: proyectos } = useProyectos();
  const { data: licitaciones } = useLicitaciones({ porPagina: 200 });

  useEffect(() => {
    refEntrada.current?.focus();
  }, []);

  const resultados = useMemo<Resultado[]>(() => {
    const texto = consulta.toLowerCase().trim();
    const lista: Resultado[] = [];

    for (const destino of DESTINOS) {
      if (!texto || destino.etiqueta.toLowerCase().includes(texto)) {
        lista.push({
          etiqueta: destino.etiqueta,
          tag: 'Ir a',
          ir: () => {
            navegar(destino.ruta);
            alCerrar();
          },
        });
      }
    }

    if (texto) {
      for (const proyecto of proyectos ?? []) {
        const buscable = `${proyecto.codigo} ${proyecto.nombre} ${proyecto.licitacion?.organismo.nombre ?? ''}`.toLowerCase();
        if (!buscable.includes(texto)) continue;

        lista.push({
          etiqueta: `${proyecto.nombre} — ${proyecto.licitacion?.organismo.nombre ?? 'sin organismo'}`,
          tag: 'Proyecto',
          ir: () => {
            navegar(`/proyectos/${proyecto.id}`);
            alCerrar();
          },
        });
      }

      for (const licitacion of licitaciones?.datos ?? []) {
        // Las adjudicadas ya aparecen como proyecto: repetirlas sería ruido.
        if (licitacion.proyecto) continue;

        const buscable = `${licitacion.codigoMercadoPublico} ${licitacion.nombre} ${licitacion.organismo.nombre}`.toLowerCase();
        if (!buscable.includes(texto)) continue;

        lista.push({
          etiqueta: `${licitacion.nombre} — ${licitacion.organismo.nombre}`,
          tag: licitacion.estado,
          ir: () => {
            navegar('/pipeline');
            alCerrar();
          },
        });
      }
    }

    return lista.slice(0, 20);
  }, [consulta, proyectos, licitaciones, navegar, alCerrar]);

  useEffect(() => {
    setIndice(0);
  }, [consulta]);

  const alTeclear = (evento: React.KeyboardEvent<HTMLInputElement>) => {
    if (evento.key === 'ArrowDown') {
      evento.preventDefault();
      setIndice((actual) => Math.min(resultados.length - 1, actual + 1));
    } else if (evento.key === 'ArrowUp') {
      evento.preventDefault();
      setIndice((actual) => Math.max(0, actual - 1));
    } else if (evento.key === 'Enter') {
      evento.preventDefault();
      resultados[indice]?.ir();
    }
  };

  return (
    <div
      className="cmdk-overlay"
      onClick={(evento) => {
        if (evento.target === evento.currentTarget) alCerrar();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Buscar y navegar"
    >
      <div className="cmdk-box">
        <input
          ref={refEntrada}
          className="cmdk-input"
          value={consulta}
          onChange={(evento) => setConsulta(evento.target.value)}
          onKeyDown={alTeclear}
          placeholder="Buscar proyectos, licitaciones o ir a una sección…"
          aria-label="Buscar"
        />

        <div className="cmdk-results">
          {resultados.length > 0 ? (
            resultados.map((resultado, i) => (
              <div
                key={`${resultado.tag}-${resultado.etiqueta}-${i}`}
                className={`cmdk-item${i === indice ? ' activo' : ''}`}
                onMouseEnter={() => setIndice(i)}
                onClick={resultado.ir}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {resultado.etiqueta}
                </span>
                <span className="cmdk-tag">{resultado.tag}</span>
              </div>
            ))
          ) : (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Sin resultados
            </div>
          )}
        </div>

        <div className="cmdk-hint">
          <span>↑↓ para navegar</span>
          <span>Enter para abrir</span>
          <span>Esc para cerrar</span>
        </div>
      </div>
    </div>
  );
}
